/* Ten Antennas — adaptive header colour, single verdict for the whole bar.

   Probe points sit under the actual controls (logo, menu, icons, burger) and
   are averaged into ONE decision: on a mixed hero the majority wins.
   Per-group colouring and scrims were both rejected as a look.

   Live during scroll. Measured cost is 0.5-1.3ms per pass, and it is capped
   to once per animation frame, so it rides along with the scroll rather than
   lagging behind it. (The homepage stutter chased earlier was reproduced on a
   theme with none of this loaded — it is not from here.) A trailing pass runs
   after SETTLE_MS so the final position is always measured, and animation and
   transition ends reschedule too, which catches Dawn's reveal.

   The rule that keeps the colour matching what the EYE sees: an element that
   is not currently visible does not vote. A hero photo still loading, or held
   at opacity 0 by a reveal animation, reads as dark in its pixel data while
   the person is looking at a white page — probing the CDN copy is faster than
   the page's own image, so without this guard the header goes white-on-white
   for the first seconds.

   Photos are probed through a 40px CDN copy, mapped through the object-fit
   transform — the band under the header is not the top of the source file.

   window.taHeaderDebug() returns per-point readings and the verdict. */
(function () {
  var DARK_THRESHOLD = 0.5;
  var SETTLE_MS = 120;

  var SPOTS = [
    '.header__heading-link',
    '.header__inline-menu',
    '.header__icons',
    'header-drawer'
  ];

  var imgCache = {};
  var headerEl = null;
  var frameQueued = false;
  var settleTimer = null;
  var lastReadout = [];
  var canvas = null;
  var ctx = null;

  function findHeader() {
    return (
      document.querySelector('.header-wrapper') ||
      document.querySelector('header.header') ||
      document.querySelector('#shopify-section-header')
    );
  }

  function relativeLuminance(r, g, b) {
    var c = [r, g, b].map(function (v) {
      v = v / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function opaqueRgb(str) {
    var m = (str || '').match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var p = m[1].split(/[,\s/]+/).filter(Boolean).map(parseFloat);
    var alpha = p.length > 3 ? p[3] : 1;
    if (!(alpha >= 0.75)) return null;
    return { r: p[0], g: p[1], b: p[2] };
  }

  /* An invisible element must not vote: what matters is what the eye sees. */
  function isVisible(el) {
    var node = el;
    var depth = 0;
    while (node && node !== document.body && depth < 10) {
      var cs = getComputedStyle(node);
      if (cs.visibility === 'hidden' || cs.display === 'none') return false;
      if (parseFloat(cs.opacity) < 0.5) return false;
      node = node.parentElement;
      depth++;
    }
    return true;
  }

  function tinySrc(src) {
    if (!src) return src;
    try {
      var url = new URL(src, window.location.href);
      if (url.searchParams.has('width') || url.pathname.indexOf('/cdn/') !== -1) {
        url.searchParams.set('width', '40');
        return url.toString();
      }
    } catch (e) {}
    return src;
  }

  function getBitmap(src) {
    if (!src) return false;
    var key = tinySrc(src);
    if (Object.prototype.hasOwnProperty.call(imgCache, key)) return imgCache[key];

    imgCache[key] = 'pending';

    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = function () {
      imgCache[key] = probe;
      schedule();
    };
    probe.onerror = function () {
      imgCache[key] = false;
      schedule();
    };
    probe.src = key;

    return 'pending';
  }

  function sampleAt(el, bitmap, x, y) {
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    var nw = bitmap.naturalWidth;
    var nh = bitmap.naturalHeight;
    if (!nw || !nh) return null;

    var cs = getComputedStyle(el);
    var scale = cs.objectFit === 'contain'
      ? Math.min(rect.width / nw, rect.height / nh)
      : Math.max(rect.width / nw, rect.height / nh);

    var offsetX = (rect.width - nw * scale) / 2;
    var offsetY = (rect.height - nh * scale) / 2;

    var sx = (x - rect.left - offsetX) / scale;
    var sy = (y - rect.top - offsetY) / scale;

    var patch = Math.max(3, Math.round(nw / 10));
    var half = patch / 2;
    var x0 = Math.max(0, Math.min(nw - 1, sx - half));
    var y0 = Math.max(0, Math.min(nh - 1, sy - half));
    var w = Math.min(patch, nw - x0);
    var h = Math.min(patch, nh - y0);
    if (w < 1 || h < 1) return null;

    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.width = 6;
      canvas.height = 6;
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }

    try {
      ctx.clearRect(0, 0, 6, 6);
      ctx.drawImage(bitmap, x0, y0, w, h, 0, 0, 6, 6);
      var data = ctx.getImageData(0, 0, 6, 6).data;
      var sum = 0;
      var count = 0;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 128) continue;
        sum += relativeLuminance(data[i], data[i + 1], data[i + 2]);
        count++;
      }
      return count ? sum / count : null;
    } catch (e) {
      return false;
    }
  }

  function pageLuma() {
    var rgb =
      opaqueRgb(getComputedStyle(document.body).backgroundColor) ||
      opaqueRgb(getComputedStyle(document.documentElement).backgroundColor);
    return rgb ? relativeLuminance(rgb.r, rgb.g, rgb.b) : 1;
  }

  function lumaAtPoint(x, y) {
    var stack = document.elementsFromPoint(x, y) || [];

    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (headerEl && headerEl.contains(el)) continue;
      if (el === document.documentElement || el === document.body) break;

      if (el.tagName === 'IMG' || el.tagName === 'VIDEO') {
        if (el.tagName === 'IMG' && (!el.complete || !el.naturalWidth)) continue;
        if (!isVisible(el)) continue;

        var src = el.tagName === 'IMG' ? el.currentSrc || el.src : el.getAttribute('poster');
        var bitmap = getBitmap(src);
        if (bitmap === 'pending') return 'pending';
        if (bitmap === false) continue;

        var value = sampleAt(el, bitmap, x, y);
        if (value === false || value === null) continue;
        return value;
      }

      var rgb = opaqueRgb(getComputedStyle(el).backgroundColor);
      if (rgb) {
        if (!isVisible(el)) continue;
        return relativeLuminance(rgb.r, rgb.g, rgb.b);
      }
    }

    return pageLuma();
  }

  function spotLuma(el) {
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    var y = Math.round(rect.top + rect.height / 2);
    var inset = Math.min(12, rect.width * 0.25);
    var xs = [
      Math.round(rect.left + inset),
      Math.round(rect.left + rect.width / 2),
      Math.round(rect.right - inset)
    ];

    var sum = 0;
    var n = 0;
    for (var i = 0; i < xs.length; i++) {
      var v = lumaAtPoint(xs[i], y);
      if (v === 'pending') continue;
      sum += v;
      n++;
    }
    return n ? sum / n : null;
  }

  function measure() {
    frameQueued = false;
    if (!headerEl) headerEl = findHeader();
    if (!headerEl) return;

    var ownBg = opaqueRgb(getComputedStyle(headerEl).backgroundColor);
    if (ownBg) {
      headerEl.classList.remove('ta-on-dark');
      lastReadout = ['header has its own opaque background'];
      return;
    }

    var notes = [];
    var sum = 0;
    var n = 0;

    for (var g = 0; g < SPOTS.length; g++) {
      var els = headerEl.querySelectorAll(SPOTS[g]);
      for (var k = 0; k < els.length; k++) {
        var luma = spotLuma(els[k]);
        if (luma === null) continue;
        sum += luma;
        n++;
        notes.push(SPOTS[g] + ' ' + luma.toFixed(2));
      }
    }

    if (!n) {
      lastReadout = notes.concat(['no readings — state unchanged']);
      return;
    }

    var avg = sum / n;
    var dark = avg < DARK_THRESHOLD;
    headerEl.classList.toggle('ta-on-dark', dark);
    lastReadout = notes.concat(['avg ' + avg.toFixed(2) + ' -> ' + (dark ? 'white' : 'black')]);
  }

  /* One pass per frame while moving, plus a trailing pass once movement stops
     so the resting position is never left on a stale reading. */
  function schedule() {
    if (!frameQueued) {
      frameQueued = true;
      requestAnimationFrame(measure);
    }
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settleTimer = null;
      measure();
    }, SETTLE_MS);
  }

  function boot() {
    headerEl = findHeader();
    if (!headerEl) return;
    headerEl.classList.add('ta-adaptive-header');
    measure();

    Array.prototype.forEach.call(document.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', schedule, { once: true });
    });
  }

  window.taHeaderDebug = function () {
    measure();
    return lastReadout;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  document.addEventListener('animationend', schedule, true);
  document.addEventListener('transitionend', schedule, true);
  setTimeout(schedule, 600);
  setTimeout(schedule, 1800);
  setTimeout(schedule, 3500);
})();
